import React, { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { Lock, ShoppingBag, ChevronLeft } from "lucide-react";
import axios from "axios";
import { useCart } from "../context/CartContext";
import { BRAND, BUNDLES } from "../mock";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const money = (n) => `$${n.toFixed(2)}`;

const Field = ({ label, ...props }) => (
  <div className="flex flex-col gap-1.5">
    {label && <label className="text-[12px] tracking-[0.06em] text-[#1A1A1A]/60">{label}</label>}
    <input
      {...props}
      className="w-full border border-[#1A1A1A]/25 rounded-sm px-4 py-3 text-[14px] bg-white outline-none focus:border-[#1A1A1A] transition-colors"
    />
  </div>
);

const Checkout = () => {
  const { items, subtotal, savings } = useCart();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [processing, setProcessing] = useState(false);
  const [form, setForm] = useState({
    email: "", first_name: "", last_name: "", address: "", apartment: "",
    city: "", state: "", zip_code: "", phone: "",
  });
  const shipping = 0;
  const total = subtotal + shipping;

  useEffect(() => {
    if (searchParams.get("canceled")) {
      toast.info("Payment canceled. Your cart is saved — you can try again anytime.");
    }
  }, [searchParams]);

  // Log a checkout attempt (for the admin dashboard) once on mount when there are items
  useEffect(() => {
    if (items.length > 0) {
      axios
        .post(`${API}/checkouts`, {
          items: items.map((i) => ({ id: i.id, title: i.title, price: i.price, qty: i.qty })),
          subtotal,
        })
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handlePlace = async (e) => {
    e.preventDefault();
    if (items.length === 0) {
      toast.error("Your cart is empty");
      return;
    }
    setProcessing(true);
    try {
      // Save the order (with shipping details) to the dashboard as pending.
      const orderRes = await axios.post(`${API}/orders`, {
        ...form,
        items: items.map((i) => ({ id: i.id, title: i.title, price: i.price, qty: i.qty })),
      });
      const orderNumber = orderRes.data.order_number;

      // Persist a lightweight receipt so /thankyou can show what they bought
      // after Stripe redirects the customer back to the site.
      try {
        localStorage.setItem(
          "flowlux_last_order",
          JSON.stringify({
            order_number: orderNumber,
            email: form.email,
            total,
            items: items.map((i) => ({ title: i.title, masks: i.masks || i.qty, price: i.price })),
            at: Date.now(),
          })
        );
      } catch (e) {}

      // Redirect to the bundle's own live Stripe Payment Link. Prefill email +
      // tag with the order number so payments can be reconciled in Stripe.
      const link = items[0]?.link || BUNDLES[0].link;
      const url = new URL(link);
      if (form.email) url.searchParams.set("prefilled_email", form.email);
      url.searchParams.set("client_reference_id", orderNumber);
      window.location.href = url.toString();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(detail);
      setProcessing(false);
    }
  };

  return (
    <main className="bg-[#F4EFE8] min-h-[70vh]">
      <div className="max-w-[1180px] mx-auto px-5 md:px-8 py-10">
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-[12px] tracking-[0.16em] uppercase text-[#1A1A1A]/60 hover:text-[#1A1A1A] mb-8">
          <ChevronLeft size={15} /> Back
        </button>

        <div className="grid lg:grid-cols-[1.3fr_1fr] gap-12">
          {/* FORM */}
          <form onSubmit={handlePlace} className="order-2 lg:order-1">
            <section className="mb-9">
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-[13px] tracking-[0.2em] uppercase">Contact</h2>
                <span className="text-[12px] text-[#1A1A1A]/50">Have an account? <a href="/#" className="underline">Log in</a></span>
              </div>
              <Field type="email" placeholder="Email address" required value={form.email} onChange={set("email")} />
            </section>

            <section className="mb-9">
              <h2 className="text-[13px] tracking-[0.2em] uppercase mb-5">Shipping Address</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field placeholder="First name" required value={form.first_name} onChange={set("first_name")} />
                <Field placeholder="Last name" required value={form.last_name} onChange={set("last_name")} />
              </div>
              <div className="mt-4"><Field placeholder="Address" required value={form.address} onChange={set("address")} /></div>
              <div className="mt-4"><Field placeholder="Apartment, suite, etc. (optional)" value={form.apartment} onChange={set("apartment")} /></div>
              <div className="grid sm:grid-cols-3 gap-4 mt-4">
                <Field placeholder="City" required value={form.city} onChange={set("city")} />
                <Field placeholder="State" required value={form.state} onChange={set("state")} />
                <Field placeholder="ZIP code" required value={form.zip_code} onChange={set("zip_code")} />
              </div>
              <div className="mt-4"><Field placeholder="Phone" value={form.phone} onChange={set("phone")} /></div>
            </section>

            <section className="mb-9">
              <div className="flex items-center gap-2 mb-2">
                <h2 className="text-[13px] tracking-[0.2em] uppercase">Payment</h2>
                <Lock size={13} className="text-[#1A1A1A]/50" />
              </div>
              <p className="text-[12px] text-[#1A1A1A]/50 mb-5">All transactions are secure and encrypted.</p>
              <div className="border border-[#1A1A1A]/25 rounded-sm bg-white px-4 py-5 flex items-start gap-3">
                <Lock size={18} className="text-[#8a6d3b] mt-0.5 shrink-0" />
                <p className="text-[13px] text-[#1A1A1A]/70 leading-relaxed">
                  You'll be redirected to our secure <strong>Stripe</strong> checkout to complete your
                  payment. Your card details are handled entirely by Stripe and never touch our servers.
                </p>
              </div>
            </section>

            <button
              type="submit"
              disabled={processing}
              data-testid="pay-now-btn"
              className="btn-lux w-full py-4 text-[12px] tracking-[0.22em] uppercase gap-2 disabled:opacity-60"
            >
              {processing ? "Redirecting to secure checkout..." : <><Lock size={13} /> Pay {money(total)}</>}
            </button>
          </form>

          {/* SUMMARY */}
          <aside className="order-1 lg:order-2">
            <div className="lg:sticky lg:top-28 bg-[#EDE6DC] rounded-sm p-6">
              <h2 className="text-[13px] tracking-[0.2em] uppercase mb-6 flex items-center gap-2">
                <ShoppingBag size={16} /> Order Summary
              </h2>
              {items.length === 0 ? (
                <p className="text-[14px] text-[#1A1A1A]/60 py-6">Your cart is empty.</p>
              ) : (
                <div className="space-y-5 mb-6">
                  {items.map((it) => (
                    <div key={it.id} className="flex gap-4">
                      <div className="relative">
                        <img src={it.image} alt={it.title} className="w-16 h-20 object-cover rounded-sm bg-white" />
                        <span className="absolute -top-2 -right-2 bg-[#1A1A1A] text-[#F4EFE8] text-[10px] h-5 w-5 rounded-full flex items-center justify-center">{it.masks || it.qty}</span>
                      </div>
                      <div className="flex-1">
                        <p className="text-[13px] leading-snug">{it.title}</p>
                      </div>
                      <span className="text-[14px] font-medium">{money(it.price * it.qty)}</span>
                    </div>
                  ))}
                </div>
              )}

              <div className="border-t border-[#1A1A1A]/15 pt-5 space-y-3">
                <div className="flex justify-between text-[14px]">
                  <span className="text-[#1A1A1A]/70">Subtotal</span>
                  <span>{money(subtotal)}</span>
                </div>
                {savings > 0 && (
                  <div className="flex justify-between text-[14px] text-[#8a6d3b]">
                    <span>Discount savings</span>
                    <span>-{money(savings)}</span>
                  </div>
                )}
                <div className="flex justify-between text-[14px]">
                  <span className="text-[#1A1A1A]/70">Shipping</span>
                  <span className="text-[#8a6d3b]">FREE</span>
                </div>
                <div className="flex justify-between text-[18px] font-medium border-t border-[#1A1A1A]/15 pt-4">
                  <span>Total</span>
                  <span>{money(total)}</span>
                </div>
              </div>

              <div className="flex items-center gap-2 mt-6 text-[12px] text-[#1A1A1A]/60">
                <Lock size={13} /> Safe, secure & encrypted checkout
              </div>
            </div>
          </aside>
        </div>
      </div>
    </main>
  );
};

export default Checkout;
